import { useEffect, useRef, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/services/api";

export interface ChatMessage {
    id: number;
    external_id: string | null;
    subject: string;
    message: string | null;
    from_field: string | null;
    to_field: string | null;
    status: string | null;
    read: boolean;
    created_at: string | null;
}

export interface Conversation {
    application_external_id: string;
    application_name: string | null;
    application_number: string | null;
    messages: ChatMessage[];
}

function formatChatDate(iso: string | null): string {
    if (!iso) return "";
    const date = new Date(iso);
    return date.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function ChatWindow({ conversation, onMessageSent }: { conversation: Conversation; onMessageSent?: () => void }) {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    const [localMessages, setLocalMessages] = useState<ChatMessage[]>(conversation.messages);

    useEffect(() => {
        setLocalMessages(conversation.messages);
    }, [conversation.messages]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [localMessages]);

    async function handleSend() {
        const trimmed = text.trim();
        if (!trimmed || sending) return;
        setSending(true);
        setSendError(null);
        try {
            await api.post("/integrations/hub/messages/send/", {
                application_external_id: conversation.application_external_id,
                message: trimmed,
            });
            const optimistic: ChatMessage = {
                id: Date.now(),
                external_id: null,
                subject: trimmed.slice(0, 80),
                message: trimmed,
                from_field: "External",
                to_field: "Internal",
                status: "New",
                read: false,
                created_at: new Date().toISOString(),
            };
            setLocalMessages((prev) => [...prev, optimistic]);
            setText("");
            onMessageSent?.();
        } catch {
            setSendError("Failed to send message. Please try again.");
        } finally {
            setSending(false);
        }
    }

    function handleKeyDown(e: { key: string; shiftKey: boolean; preventDefault: () => void }) {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }

    return (
        <div className="flex flex-col" style={{ height: "100%" }}>
            <div className="flex-1 overflow-y-auto space-y-3 px-1 py-2" style={{ minHeight: 0 }}>
                {localMessages.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-8">No messages yet. Start the conversation below.</p>
                )}
                {localMessages.map((msg) => {
                    const isOutbound = msg.from_field === "External";
                    return (
                        <div key={msg.id} className={`flex ${isOutbound ? "justify-end" : "justify-start"}`}>
                            <div className="max-w-[80%] space-y-1">
                                <div
                                    className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                                        isOutbound
                                            ? "bg-primary text-white rounded-br-sm"
                                            : "bg-slate-100 text-slate-800 rounded-bl-sm"
                                    }`}
                                >
                                    {msg.message || msg.subject}
                                </div>
                                <p className={`text-[10px] text-slate-400 px-1 ${isOutbound ? "text-right" : "text-left"}`}>
                                    {isOutbound ? "External" : "Internal"} · {formatChatDate(msg.created_at)}
                                </p>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            <div className="shrink-0 pt-3 mt-3 border-t border-slate-100 space-y-2">
                {sendError && <p className="text-xs text-red-500 font-medium">{sendError}</p>}
                <p className="text-[10px] text-slate-400">{text.length} / 500 characters</p>
                <div className="flex gap-2 items-end">
                    <textarea
                        className="flex-1 rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-primary outline-none px-4 py-3 text-sm resize-none transition focus:ring-2 focus:ring-primary/20"
                        rows={2}
                        placeholder="type here..."
                        maxLength={500}
                        value={text}
                        onChange={(e: { target: { value: string } }) => setText(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <Button
                        onClick={handleSend}
                        disabled={sending || !text.trim()}
                        className="h-10 px-4 rounded-2xl bg-primary hover:bg-[#002b66] font-bold flex items-center gap-1.5 shrink-0"
                    >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {sending ? "" : "Send"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
